import type { CSSProperties } from "react";
import { useEffect, useState } from "react";

import {
  fetchAdminAction,
  summarizeActionResult,
  type ActionSummary,
} from "../../lib/admin/adminActions.mjs";
import { getSupabase } from "../../lib/supabase";

/**
 * MER-487 PR C: inline status panel for a queued admin action.
 *
 * Polls the staff-readable admin_action row every 4 seconds until the
 * droplet poller (PR B) drives it to a terminal status (succeeded,
 * failed, or cancelled). Renders the per-step rollup, the failure
 * detail when there is one, and a prominent link to the bot-authored
 * config PR for actions that end in one (customer_create, config_edit).
 *
 * A row that is not visible yet (replication lag, or the insert
 * landing a beat after the RPC returns) summarizes to status
 * "unknown" / non-terminal, so the panel just keeps polling.
 *
 * Poll cap (review fix): after MAX_STALE_TICKS consecutive
 * non-terminal or error ticks (~3 minutes at 4s), the panel stops
 * polling and offers a Retry button that resets the counter and
 * resumes watching. The action itself keeps running on the droplet;
 * the cap only stops the browser-side polling.
 */

const POLL_INTERVAL_MS = 4000;
const MAX_STALE_TICKS = 45;

const STATUS_COPY: Record<ActionSummary["status"], string> = {
  pending: "Queued, waiting to start",
  running: "Running",
  succeeded: "Completed",
  failed: "Failed",
  cancelled: "Cancelled",
  unknown: "Waiting for status",
};

function statusTone(status: string): { bg: string; fg: string } {
  if (status === "succeeded" || status === "success") {
    return { bg: "rgba(46,160,67,0.12)", fg: "var(--flag-green)" };
  }
  if (status === "failed" || status === "error") {
    return { bg: "rgba(192,57,43,0.12)", fg: "var(--flag-red)" };
  }
  if (status === "running") {
    return { bg: "rgba(255,193,7,0.16)", fg: "var(--flag-yellow)" };
  }
  return { bg: "rgba(0,0,0,0.06)", fg: "var(--text-muted)" };
}

export default function ActionStatusPanel(props: {
  actionId: string;
  onSummary?: (summary: ActionSummary) => void;
}) {
  const { actionId, onSummary } = props;
  const [summary, setSummary] = useState<ActionSummary | null>(null);
  const [pollError, setPollError] = useState<string | null>(null);
  const [stalled, setStalled] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;
    // Consecutive non-terminal or error ticks. Only Retry (which
    // restarts this effect via retryNonce) resets it.
    let staleTicks = 0;

    function scheduleOrStall() {
      if (staleTicks >= MAX_STALE_TICKS) {
        setStalled(true);
        return;
      }
      timer = window.setTimeout(poll, POLL_INTERVAL_MS);
    }

    async function poll() {
      try {
        const row = await fetchAdminAction(getSupabase(), actionId);
        if (cancelled) return;
        const next = summarizeActionResult(row);
        setSummary(next);
        setPollError(null);
        onSummary?.(next);
        if (next.terminal) return;
        staleTicks += 1;
        scheduleOrStall();
      } catch (err) {
        if (cancelled) return;
        // Transient read failures should not kill the panel mid-run;
        // surface the message and try again on the next tick.
        setPollError(err instanceof Error ? err.message : String(err));
        staleTicks += 1;
        scheduleOrStall();
      }
    }

    setSummary(null);
    setPollError(null);
    setStalled(false);
    poll();
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
    // onSummary is intentionally not a dependency: parents pass inline
    // callbacks and re-subscribing on every render would restart the
    // poll loop from scratch on each parent re-render. retryNonce IS a
    // dependency: bumping it restarts the loop with a fresh counter.
  }, [actionId, retryNonce]);

  const status = summary?.status ?? "unknown";
  const tone = statusTone(status);

  return (
    <div style={wrapStyle}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span
          style={{
            fontSize: 10,
            padding: "3px 10px",
            borderRadius: 10,
            background: tone.bg,
            color: tone.fg,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            fontWeight: 700,
          }}
        >
          {status === "unknown" ? "Pending" : status}
        </span>
        <span style={{ fontSize: 12, color: "var(--text-2)" }}>
          {STATUS_COPY[summary?.status ?? "unknown"]}
          {!stalled && summary && !summary.terminal ? " (checking every few seconds)" : ""}
        </span>
      </div>

      {pollError && !stalled ? (
        <div style={{ fontSize: 11, color: "var(--flag-yellow)" }}>
          Could not check status just now ({pollError}). Retrying.
        </div>
      ) : null}

      {stalled ? (
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: "var(--text-2)" }}>
            Still waiting. The action may still complete; use Retry to resume watching.
          </span>
          <button
            type="button"
            onClick={() => setRetryNonce((n) => n + 1)}
            style={retryButtonStyle}
          >
            Retry
          </button>
        </div>
      ) : null}

      {summary && summary.steps.length > 0 ? (
        <div style={{ display: "grid", gap: 4 }}>
          {summary.steps.map((step, i) => {
            const stepTone = statusTone(step.status);
            return (
              <div
                key={`${step.step}-${i}`}
                style={{
                  display: "grid",
                  gridTemplateColumns: "90px 1fr",
                  gap: 8,
                  fontSize: 12,
                  alignItems: "baseline",
                }}
              >
                <span
                  style={{
                    color: stepTone.fg,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    fontSize: 10,
                    letterSpacing: 0.4,
                  }}
                >
                  {step.status}
                </span>
                <span style={{ color: "var(--text)" }}>
                  {step.step}
                  {step.detail ? (
                    <span style={{ color: "var(--text-muted)" }}> ({step.detail})</span>
                  ) : null}
                </span>
              </div>
            );
          })}
        </div>
      ) : null}

      {summary?.status === "failed" ? (
        <div style={{ display: "grid", gap: 4 }}>
          <div style={{ fontSize: 12, color: "var(--flag-red)", whiteSpace: "pre-wrap" }}>
            {summary.error ?? "The action failed without an error message."}
          </div>
          <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
            You can submit the same request again. Steps that already completed are skipped, so
            re-running is safe.
          </div>
        </div>
      ) : null}

      {summary?.configPrUrl ? (
        <div
          style={{
            padding: "10px 12px",
            borderRadius: 8,
            background: "rgba(38,168,159,0.10)",
            border: "1px solid var(--brand-line)",
            fontSize: 13,
          }}
        >
          <a
            href={summary.configPrUrl}
            target="_blank"
            rel="noreferrer"
            style={{ color: "var(--brand-deep)", fontWeight: 700 }}
          >
            Merge the config PR to finish onboarding
          </a>
          <div style={{ fontSize: 11, color: "var(--text-2)", marginTop: 2 }}>
            A pull request was opened with this customer's config. Review and merge it to apply the
            change.
          </div>
        </div>
      ) : null}
    </div>
  );
}

const retryButtonStyle: CSSProperties = {
  padding: "4px 14px",
  borderRadius: 8,
  border: "1px solid var(--border-subtle)",
  background: "var(--bg-card)",
  color: "var(--text)",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
};

const wrapStyle: CSSProperties = {
  display: "grid",
  gap: 10,
  padding: "12px 14px",
  border: "1px solid var(--border-subtle)",
  borderRadius: 8,
  background: "var(--bg-card)",
};
